import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Project from '../project/ProjectBox';
import './ProjectsYouMightLike.css';
import GlobalOrganizationsProjects from '../global/GlobalOrganizationsProjects';
import { setAllProjects, setDisplayProjects } from '../../services/store/GenericReducer';
import { sortProjectsByDisplayOrder } from '../../services/filters/filters';

/**
 * ProjectsYouMightLike - Reusable component that displays suggested projects
 * Shows first 6 projects from the sorted allProjects list
 * Can exclude a specific project (e.g., the currently viewed project)
 *
 * @param {string} excludeProjectId - Optional project ID to exclude from the list
 * @param {string} title - Optional custom title (default: "אולי יעניין אותך")
 * @param {number} limit - Optional number of projects to show (default: 6)
 */
export default function ProjectsYouMightLike({ excludeProjectId, title = "אולי יעניין אותך", limit = 6 }) {
    const store = useSelector(state => state.reducer);
    const dispatch = useDispatch();
    const [localProjects, setLocalProjects] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    // Fetch projects if store is empty
    useEffect(() => {
        const fetchProjects = async () => {
            // Only fetch if store doesn't have projects
            if (store.allProjects?.length > 0) return;

            setIsLoading(true);
            try {
                const organizationsProjects = await GlobalOrganizationsProjects();
                const projects = organizationsProjects.projects || [];
                if (projects.length > 0) {
                    dispatch(setAllProjects(projects));
                    const sortedProjects = sortProjectsByDisplayOrder(projects);
                    dispatch(setDisplayProjects(sortedProjects));
                    setLocalProjects(sortedProjects);
                }
            } catch (error) {
                console.error('Error fetching projects:', error);
            }
            setIsLoading(false);
        };

        fetchProjects();
    }, [store.allProjects, dispatch]);

    // Get projects from store or local state
    const allProjects = store.displayProjects?.length > 0
        ? store.displayProjects
        : store.allProjects?.length > 0
            ? store.allProjects
            : localProjects;

    // Filter out the excluded project and take first 'limit' projects
    const suggestedProjects = allProjects
        .filter(project => project._id !== excludeProjectId)
        .slice(0, limit);

    // Don't render if no projects available and not loading
    if (suggestedProjects.length === 0 && !isLoading) {
        return null;
    }

    return (
        <section className="projects-you-might-like card-section cards-slider">
            <div className="content-container">
                <h2 className="section-title">{title}</h2>
                <div className="card-wrapper">
                    {suggestedProjects.map((project, index) => (
                        <Project
                            key={project._id || index}
                            project_fields={project}
                        />
                    ))}
                </div>
            </div>
        </section>
    );
}
